/**
 * How the trial form reads the server's answers (`apps/server` routes/trial-signup.ts), kept apart
 * from the component so the retry rules are tested without a browser.
 *
 * The create runs inside the request, so the form can lose its answer — our timeout, a proxy cut,
 * a dropped connection — to a signup that went through. It never guesses: it asks the status
 * route, keyed on the same verified number, until the organisation is ready, the server says
 * nothing was made (submit again), or it runs out of patience and offers a "Check again" button.
 * A submit for a number that is already set up answers `alreadySetUp` with the username instead
 * of refusing, and one still being made answers 202 `pending`.
 */

export type TrialResult = {
  name: string;
  username: string;
  trialEndsAt: number;
  welcomeEmailSent?: boolean;
  alreadySetUp?: boolean;
};

export type TrialOutcome =
  | { kind: 'done'; result: TrialResult }
  /** An earlier attempt is still making the organisation: ask the status route. */
  | { kind: 'pending' }
  /** Nothing was made for this number: submitting the form again is safe. */
  | { kind: 'none' }
  /** The phone verification is too old for the server: a fresh code is needed. */
  | { kind: 'reverify'; message: string }
  | { kind: 'error'; message: string };

type Body = Partial<TrialResult> & { error?: string; pending?: boolean; state?: string };

const isResult = (body: Body): body is TrialResult =>
  typeof body.username === 'string' && typeof body.name === 'string';

/** The answer to the signup itself: 201 created, 200 already set up, 202 still being made. */
export function readSignupAnswer(status: number, body: Body): TrialOutcome {
  if (status === 202 || body.pending) return { kind: 'pending' };
  if (status === 401) {
    return { kind: 'reverify', message: body.error || 'Please verify your mobile number again.' };
  }
  if (status >= 200 && status < 300 && isResult(body)) return { kind: 'done', result: body };
  return {
    kind: 'error',
    message: body.error || 'We could not start your trial. Please try again.',
  };
}

/** The answer to the status route: `ready`, `pending` or `none`. */
export function readStatusAnswer(status: number, body: Body): TrialOutcome {
  if (status === 401) {
    return { kind: 'reverify', message: body.error || 'Please verify your mobile number again.' };
  }
  if (status !== 200) {
    return { kind: 'error', message: body.error || 'We could not check on your trial just now.' };
  }
  if (body.state === 'ready' && isResult(body)) {
    return { kind: 'done', result: { ...body, alreadySetUp: true } };
  }
  if (body.state === 'none') return { kind: 'none' };
  return { kind: 'pending' };
}

export type WaitOptions = {
  /** One status request; a thrown error (network) counts as "not known yet". */
  check: () => Promise<TrialOutcome>;
  sleep: (ms: number) => Promise<void>;
  attempts: number;
  intervalMs: number;
};

/**
 * Asks until the answer is settled, waiting `intervalMs` before each ask. `unknown` when every
 * attempt came back pending or failed — the form then offers "Check again" rather than a verdict.
 */
export async function waitForTrial({
  check,
  sleep,
  attempts,
  intervalMs,
}: WaitOptions): Promise<TrialOutcome | { kind: 'unknown' }> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    await sleep(intervalMs);
    let outcome: TrialOutcome;
    try {
      outcome = await check();
    } catch {
      continue;
    }
    if (outcome.kind !== 'pending' && outcome.kind !== 'error') return outcome;
  }
  return { kind: 'unknown' };
}
